import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Truck, MapPin, Package, Clock, Shield, Smartphone, 
  Heart, CheckCircle, ArrowRight, MessageCircle 
} from 'lucide-react';
import { InlineContactForm } from '@/components/InlineContactForm';

const DELIVERY_IMAGE = 'https://customer-assets.emergentagent.com/job_18b8bc6b-a5e3-4abe-a393-25b68282275d/artifacts/5s6yt2u1_image.png';

// Technology Partners
const TECHNOLOGY_PARTNERS = [
  {
    name: 'Bob Go',
    logo: '/bobgo-logo.png',
    url: 'https://www.bobgo.co.za',
  },
  {
    name: 'Bob Box',
    logo: '/bobbox-logo.png',
    url: 'https://www.bobbox.co.za',
  },
  {
    name: 'Bob Pay',
    logo: '/bobpay-logo.png',
    url: 'https://www.bobpay.co.za',
  },
  {
    name: 'Ship Logic',
    logo: '/shiplogic-logo.png',
    url: 'https://www.shiplogic.com',
  },
];

// Customer experience delivery highlights
const DELIVERY_HIGHLIGHTS = [
  { label: 'Professional Riders' },
  { label: 'Live Tracking' },
  { label: 'ETA Notifications' },
  { label: 'Customer Support' },
  { label: 'Estate Access Assistance' },
  { label: 'Returns & Exchanges' },
  { label: '3-5 Minute Call Centre Response' },
  { label: '24-Hour Gauteng Deliveries' },
  { label: '48-Hour Cape Town Deliveries' },
];

const features = [
  {
    icon: MapPin,
    title: 'Live Tracking',
    description: 'Real-time GPS tracking for every parcel with automatic customer updates.',
    color: 'bg-blue-100 text-blue-600',
  },
  {
    icon: Clock,
    title: 'Fast Delivery',
    description: 'Same-day and next-day options for urgent orders across Gauteng.',
    color: 'bg-amber-100 text-amber-600',
  },
  {
    icon: Package,
    title: 'Premium Handling',
    description: 'Specialized care for parcels up to 2kg including fragile items.',
    color: 'bg-green-100 text-green-600',
  },
  {
    icon: Shield,
    title: 'Customer Experience',
    description: 'Customers receive live tracking, ETA updates and delivery progress notifications throughout their delivery journey.',
    color: 'bg-purple-100 text-purple-600',
  },
  {
    icon: Smartphone,
    title: 'Auto Notifications',
    description: 'Automated delivery updates keep customers informed throughout.',
    color: 'bg-red-100 text-drop24-red',
  },
  {
    icon: Heart,
    title: 'Care Culture',
    description: 'Trained professionals who treat every parcel as their own.',
    color: 'bg-pink-100 text-pink-600',
  },
];

const serviceAreas = [
  { region: 'Gauteng', available: true },
  { region: 'Western Cape', available: true },
  { region: 'Durban', available: false, comingSoon: 'Coming soon in 2027' }
];

// GTM tracking
const trackEvent = (eventName, eventData = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...eventData });
  }
};

export function DeliveryPage() {
  return (
    <div data-testid="delivery-page">
      {/* Hero - MLM Style */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Last-Mile Delivery</span>
              <h1 
                data-testid="delivery-hero-title"
                className="font-serif text-5xl md:text-6xl text-gray-900 leading-tight mt-4 mb-6"
              >
                Customer Experience Delivery for <span className="text-gradient">Online Stores</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                We provide a branded customer experience throughout the delivery journey. 
                Your brand experience doesn't end at checkout.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact-form"
                  data-testid="delivery-cta-primary"
                  className="inline-flex items-center gap-2 bg-drop24-red text-white hover:bg-[#c4191f] px-8 py-4 rounded-full font-semibold transition-all shadow-soft"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Let's Design Your Logistics
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="https://wa.me/27660719068"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('contact_click', { contact_type: 'whatsapp', location: 'delivery_hero' })}
                  className="inline-flex items-center gap-2 bg-white text-gray-700 hover:bg-gray-50 px-8 py-4 rounded-full font-semibold border border-gray-200"
                >
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  WhatsApp Us
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <img
                src={DELIVERY_IMAGE}
                alt="Delivery Rider"
                className="w-full rounded-3xl shadow-soft-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technology Partners */}
      <section className="py-16 bg-gray-50" data-testid="technology-partners-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Integrations</span>
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mt-4">
              Technology <span className="text-gradient">Partners</span>
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              Drop24 integrates seamlessly with leading ecommerce platforms including <strong>Bob Go</strong> for courier management, <strong>Bob Pay</strong> for payment solutions, <strong>Bob Box</strong> for smart locker deliveries, and <strong>Ship Logic</strong> for shipping automation.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {TECHNOLOGY_PARTNERS.map((partner, index) => (
              <motion.a
                key={index}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100"
                data-testid={`partner-${partner.name.toLowerCase().replace(' ', '-')}`}
                onClick={() => {
                  trackEvent('partner_click', { partner_name: partner.name, partner_url: partner.url });
                  if (window.gtag) {
                    window.gtag('event', 'click', {
                      event_category: 'outbound',
                      event_label: partner.name,
                      transport_type: 'beacon',
                      event_callback: function() {}
                    });
                  }
                }}
              >
                <img
                  src={partner.logo}
                  alt={`${partner.name} logo`}
                  className="h-12 w-40 object-contain transition-all duration-300"
                />
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid - MLM Style */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Features</span>
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mt-4">
              Built for <span className="text-gradient">Ecommerce</span> Brands
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-3xl p-8 shadow-soft card-shadow-hover border border-gray-100"
              >
                <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="font-semibold text-xl text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Extension Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Brand Extension</span>
              <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mt-4 mb-6">
                Drop24 is Not the Brand. <span className="text-gradient">You Are.</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We understand that your delivery person is often the only human touchpoint 
                your customer has with your brand. That's why we train our riders to represent you, not us.
              </p>
              <ul className="space-y-4">
                {[
                  'Unbranded or neutral delivery vehicles',
                  'Professional handover protocols',
                  'Live tracking and ETA notifications for every delivery',
                  'Responsive customer support and estate access assistance',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-drop24-red flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 md:p-10 shadow-soft"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-drop24-red-light rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-drop24-red" />
                </div>
                <h3 className="font-serif text-2xl text-gray-900">Why It Matters</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Research shows that delivery experience directly impacts repeat purchase rates 
                and brand perception.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-2xl">
                  <p className="font-serif text-4xl text-drop24-red">72%</p>
                  <p className="text-sm text-gray-600 mt-1">won't reorder after bad delivery</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-2xl">
                  <p className="font-serif text-4xl text-drop24-red">86%</p>
                  <p className="text-sm text-gray-600 mt-1">share positive experiences</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Customer Experience Delivery */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h3 className="font-serif text-3xl text-gray-900">
                Customer Experience <span className="text-gradient">Delivery</span>
              </h3>
              <p className="text-gray-600 mt-3 max-w-2xl mx-auto">Drop24 focuses on customer experience rather than parcel branding. Our riders represent your brand through professionalism, communication, reliability and customer service.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {DELIVERY_HIGHLIGHTS.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-soft border border-gray-100"
                  data-testid={`delivery-highlight-${index}`}
                >
                  <CheckCircle className="w-5 h-5 text-drop24-red flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{item.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-drop24-red font-semibold text-sm uppercase tracking-wider">Coverage</span>
              <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mt-4 mb-6">
                Where We <span className="text-gradient">Deliver</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Currently serving Gauteng and the Western Cape, with Durban coming soon in 2027.
              </p>
              
              <div className="space-y-4">
                {serviceAreas.map((area, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center gap-3 p-4 rounded-2xl ${
                      area.available 
                        ? 'bg-gray-50' 
                        : 'bg-drop24-yellow/10 border border-drop24-yellow/30'
                    }`}
                  >
                    {area.available ? (
                      <MapPin className="w-5 h-5 text-drop24-red" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-600" />
                    )}
                    <span className="text-gray-900 font-medium">{area.region}</span>
                    {area.comingSoon && (
                      <span className="text-gray-500 text-sm">— Coming {area.comingSoon}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gray-900 rounded-3xl p-8 md:p-10 text-white"
            >
              <h3 className="font-serif text-2xl mb-4">Need delivery outside our areas?</h3>
              <p className="text-gray-300 mb-6">
                Contact us for custom solutions. We're always expanding our network.
              </p>
              <Link
                to="/contact"
                data-testid="delivery-contact-link"
                className="inline-flex items-center gap-2 bg-drop24-red text-white hover:bg-[#c4191f] px-6 py-3 rounded-full font-semibold transition-all"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Inline Contact Form */}
      <InlineContactForm 
        preselectedInterest="delivery"
        title="Let's Design Your Logistics"
        subtitle="Ready to deliver your brand the way you would? Fill in the form below and we'll get back to you within 24 hours."
      />
    </div>
  );
}

export default DeliveryPage;
